import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  INDICATOR_MANAGEMENT_SUMMARY,
  PAGE_SIZE_OPTIONS,
} from "../../utils/constants";
import TabComponent from "../../components/molecule/Tab/TabComponent";
import { getIndicator } from "../../services/Indicator/getIndicator";
import { getAlertSan } from "../../services/AlertSan/getAlertSan";
import IndicatorManagementSearchTable from "./Indicator/IndicatorManagementSearchTable";
import IndicatorAlerts from "./Alert/IndicatorAlerts";
import { Button, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import { PaginationValues } from "../../types/General/PaginationValues";
import { useFilter } from "../../hooks/filter/useFilter";
import { useForm } from "react-hook-form";
import useTableRendering from "../../hooks/table/useTableRendering";
import { TableConfig } from "../../types/Components/Table/TableConfig";
import TableIndicatorManagement from "./Indicator/TableIndicatorManagement";
import { useCustomModal } from "../../hooks/modal/useCustomModal";
import { PaginationConfig } from "../../types/Components/Table/PaginationConfig";
import { ModalConfig } from "../../types/Components/Modal/ModalConfig";
import { useAuth } from "../../provider/AuthProvider";
import IndicatorAlertSan from "./Alert/IndicatorAlertSan";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertSanSchema } from "./data/AlertSanSchema";
import { useAlertSan } from "./hooks/useAlertSan";
import { useIndicatorAlerts } from "./hooks/useIndicatorAlerts";

const IndicatorManagement: FC = () => {
  const navigate = useNavigate();

  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoadedAlertSan, setIsLoadedAlertSan] = useState(false);
  const [, setErrorFindingData] = useState(false);
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [originalDataTable, setOriginalDataTable] = useState<any[]>([]);
  const [indicatorAlertsDataTable, setIndicatorAlertsDataTable] = useState<
    any[]
  >([]);
  const [
    originalIndicatorAlertsDataTable,
    setOriginalIndicatorAlertsDataTable,
  ] = useState<any[]>([]);
  const [alertSanDataTable, setAlertSanDataTable] = useState<any[]>([]);
  const [originalAlertSanDataTable, setOriginalAlertSanDataTable] = useState<
    any[]
  >([]);
  const [isSearching, setIsSearching] = useState<boolean>(true);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [indicatorAlertsPageSize, setIndicatorAlertsPageSize] = useState(
    PAGE_SIZE_OPTIONS[0],
  );
  const [alertSanPageSize, setAlertSanPageSize] = useState(
    PAGE_SIZE_OPTIONS[0],
  );
  const [totalItems, setTotalItems] = useState<number>(0);
  const [indicatorAlertsTotalItems, setIndicatorAlertsTotalItems] =
    useState<number>(0);
  const [alertSanTotalItems, setAlertSanTotalItems] = useState<number>(0);
  const [isLoadedIndicatorAlerts, setIsLoadedIndicatorAlerts] = useState(false);
  const [idItem, setIdItem] = useState<number>(0);
  const { control, setValue } = useForm();
  const {
    control: controlAlertSan,
    setValue: setValueAlertSan,
    watch: watchAlertSan,
    handleSubmit: handleSubmitAlertSan,
    reset: resetAlertSan,
    formState: { errors: errorsAlertSan },
  } = useForm({
    resolver: zodResolver(AlertSanSchema),
  });
  const { auth } = useAuth();
  const user = auth.user;

  const filterNames = useMemo(
    () => ["IndicatorId", "PartnerId", "LevelId", "StateId", "FrequencyId"],
    [],
  );

  const { filters, setFilter } = useFilter({ setValue, filterNames });
  const { isOpen, title, buttonTitle, openModal, closeModal } = useCustomModal(
    "",
    "",
  );

  const {
    paginationConfigIndicatorAlerts,
    tableConfigIndicatorAlerts,
    indicatorAlertsCurrentPage,
  } = useIndicatorAlerts({
    user,
    setIsSearching,
    indicatorAlertsDataTable,
    originalIndicatorAlertsDataTable,
    setIndicatorAlertsDataTable,
    isLoadedIndicatorAlerts,
    indicatorAlertsPageSize,
    setIndicatorAlertsPageSize,
    indicatorAlertsTotalItems,
  });

  const {
    paginationConfigAlertSan,
    modalConfigAlertSan,
    tableConfigAlertaSan,
    alertSanCurrentPage,
  } = useAlertSan({
    user,
    setIsSearching,
    alertSanDataTable,
    originalAlertSanDataTable,
    setAlertSanDataTable,
    isLoadedAlertSan,
    alertSanPageSize,
    setAlertSanPageSize,
    alertSanTotalItems,
    setIdItem,
    setValueAlertSan,
  });

  useEffect(() => {
    const fetchData = (
      filterValues?: any,
      paginationValues?: PaginationValues,
      paginationValuesIndicatorAlerts?: PaginationValues,
      paginationValuesAlertSan?: PaginationValues,
    ) => {
      setErrorFindingData(false);
      setIsLoaded(false);
      setIsLoadedIndicatorAlerts(false);
      setIsLoadedAlertSan(false);
      if (user?.role?.level?.id === 2 && user?.partner) {
        setFilter("PartnerId", user.partner.id.toString());
        filterValues = { ...filterValues, PartnerId: user.partner.id };
      }

      setTimeout(() => {
        getIndicator(undefined, filterValues, paginationValues)
          .then((result) => {
            setDataTable(result.content);
            setOriginalDataTable(result.content);
            setTotalItems(result.totalItems);
            setIsLoaded(true);
            setIsSearching(false);
          })
          .catch((error) => {
            console.log("API Error:", error);
            setErrorFindingData(true);
            setIsSearching(false);
          });
        getIndicator(undefined, undefined, paginationValuesIndicatorAlerts)
          .then((result) => {
            setIsLoadedIndicatorAlerts(true);
            setIndicatorAlertsDataTable(result.content);
            setOriginalIndicatorAlertsDataTable(result.content);
            setIndicatorAlertsTotalItems(result.totalItems);
            setIsSearching(false);
          })
          .catch((error) => {
            console.log("API Error:", error);
          });
        getAlertSan(undefined, undefined, paginationValuesAlertSan)
          .then((result) => {
            setIsSearching(false);
            setIsLoadedAlertSan(true);
            setAlertSanDataTable(result.content);
            setOriginalAlertSanDataTable(result.content);
            setAlertSanTotalItems(result.totalItems);
          })
          .catch((error) => {
            console.log("API Error:", error);
          });
      }, 1000);
    };

    isSearching &&
      fetchData(
        filters,
        {
          CurrentPage: currentPage,
          PageSize: pageSize,
        },
        {
          CurrentPage: indicatorAlertsCurrentPage,
          PageSize: indicatorAlertsPageSize,
        },
        {
          CurrentPage: alertSanCurrentPage,
          PageSize: alertSanPageSize,
        },
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching]);

  const tableConfig: TableConfig = {
    tableName: "Lista de indicadores",
    dataTable,
    originalDataTable,
    setDataTable,
    renderTable: (dataTable) => (
      <TableIndicatorManagement
        dataTable={dataTable}
        isLoaded={isLoaded}
        setValue={setValue}
        openModal={openModal}
        user={user}
      />
    ),
    isLoaded,
  };

  const { currentPage, totalPages, handlePageChange, renderContent } =
    useTableRendering(tableConfig, pageSize, totalItems, setIsSearching);

  const paginationConfig: PaginationConfig = {
    pageSize,
    currentPage,
    totalPages,
    handlePageChange,
    renderContent,
    setPageSize,
    totalItems,
  };

  const modalConfig: ModalConfig = {
    isOpen,
    title,
    buttonTitle,
    openModal,
    closeModal,
  };

  const handleAddNewItem = () => {
    navigate("/gestao-de-indicadores/criar-indicadores");
  };

  const handleCarregarDadosSISAN = () => {
    navigate("/gestao-de-indicadores/carregar-dados-no-sisan");
  };

  const tabs = ["Indicadores do sistema"];
  const panels = [
    <IndicatorManagementSearchTable
      key="indicator-table"
      user={user}
      setIsSearching={setIsSearching}
      filters={filters}
      filterNames={filterNames}
      setFilter={setFilter}
      tableConfig={tableConfig}
      paginationConfig={paginationConfig}
      modalConfig={modalConfig}
      control={control}
    />,
  ];

  if (user?.permissions && user.permissions.includes("EditIndicators")) {
    tabs.push("Factor de alerta dos indicadores");
    panels.push(
      <IndicatorAlerts
        tableConfig={tableConfigIndicatorAlerts}
        paginationConfig={paginationConfigIndicatorAlerts}
      />,
    );
  }

  if (user?.permissions && user.permissions.includes("CreateAlertSan")) {
    tabs.push("Alertas SAN");
    panels.push(
      <IndicatorAlertSan
        user={user}
        setIsSearching={setIsSearching}
        paginationConfig={paginationConfigAlertSan}
        setIdItem={setIdItem}
        idItem={idItem}
        tableConfig={tableConfigAlertaSan}
        modalConfig={modalConfigAlertSan}
        control={controlAlertSan}
        setValue={setValueAlertSan}
        watch={watchAlertSan}
        handleSubmit={handleSubmitAlertSan}
        reset={resetAlertSan}
        errors={errorsAlertSan}
      />,
    );
  }

  return (
    <TabComponent
      tabs={tabs}
      panels={panels}
      headingElement={
        <>
          <Flex direction="row" justifyContent="space-between">
            <Heading as="h1">Gestão de indicadores</Heading>
            <Stack direction="row" gap={4}>
              {user.permissions.includes("CreateIndicatorData") && (
                <Button variant="secondary" onClick={handleCarregarDadosSISAN}>
                  Carregar dados no SISAN (Excel)
                </Button>
              )}

              {user.permissions.includes("CreateIndicators") && (
                <Button variant="primary" onClick={handleAddNewItem}>
                  Criar indicador
                </Button>
              )}
            </Stack>
          </Flex>

          <Text>{INDICATOR_MANAGEMENT_SUMMARY}</Text>
        </>
      }
    />
  );
};

export default IndicatorManagement;
